import React, { createContext, useContext, useEffect, useState } from 'react';
import { HeaderMenuStore } from '../stores/header-menu-store';

/**
 * Context value for the HeaderMenu
 * Provides access to the MST store managing navigation state
 */

const HeaderMenuContext = /*#__PURE__*/createContext(null);

/**
 * Props for the HeaderMenuProvider
 */

/**
 * Provider component that creates and manages the HeaderMenuStore
 * Wraps children with access to the navigation store via context
 */
export const HeaderMenuProvider = ({
  children,
  navigationConfig,
  autoInitialize = true,
  useEagerLoading = false
}) => {
  if (!navigationConfig) {
    throw new Error('HeaderMenuProvider requires navigationConfig prop');
  }

  // Create store instance once and configure it
  const [store] = useState(() => {
    const storeInstance = HeaderMenuStore.create({});
    storeInstance.setConfig(navigationConfig);
    return storeInstance;
  });

  // Initialize the store when autoInitialize is true
  useEffect(() => {
    if (autoInitialize) {
      // Note: We don't need to await here because the store tracks loading state
      // via isEagerLoadingComplete, and MobX will trigger re-renders when it changes
      if (useEagerLoading) {
        void store.initializeWithEagerLoading();
      } else {
        void store.initialize();
      }
    }
  }, [store, autoInitialize, useEagerLoading]);
  const contextValue = {
    store
  };
  return /*#__PURE__*/React.createElement(HeaderMenuContext.Provider, {
    value: contextValue
  }, children);
};

/**
 * Hook to access the HeaderMenuStore from context
 * Must be used within a HeaderMenuProvider
 * @returns The HeaderMenuStore instance
 * @throws Error if used outside of HeaderMenuProvider
 */
export const useHeaderMenuStore = () => {
  const context = useContext(HeaderMenuContext);
  if (!context) {
    throw new Error('useHeaderMenuStore must be used within a HeaderMenuProvider');
  }
  return context.store;
};
//# sourceMappingURL=header-menu-context.js.map